import { MaterialIcons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { useState } from 'react';
import { StyleSheet } from 'react-native';
import Animated from 'react-native-reanimated';

import useOnLightRatingPress from '@/hooks/useOnLightRatingPress';
import { firebaseImage, ThumbSize } from '@/utils/firebaseImage';

import LightRatingButton from '../buttons/LightRatingButton';
import Gallery from './Gallery';

interface DetailsHeaderGalleryProps {
  thumbnails: string[];
  animatedOpacityStyle: { opacity: number };
  lightRatingValues?: { ranking: number | undefined; name: string };
}

const DetailsHeaderGallery = ({ thumbnails, lightRatingValues }: DetailsHeaderGalleryProps) => {
  const [showGallery, setShowGallery] = useState<boolean>(false);
  const [initialImage, setInitialImage] = useState<number>(0);

  const { onLightRatingPress } = useOnLightRatingPress();

  const handleOpenGallery = (initialImage: number) => {
    setInitialImage(initialImage);
    setShowGallery(true);
  };

  return (
    <>
      <Gallery
        thumbnails={thumbnails}
        initialImage={initialImage}
        showGallery={showGallery}
        hideGallery={() => setShowGallery(false)}
      />
      <div className="w-full flex flex-row sm:rounded-xl lg:rounded-3xl overflow-hidden relative gap-x-2">
        {/* Main Image */}
        <button className="w-1/2 relative" onClick={() => handleOpenGallery(0)}>
          {lightRatingValues && thumbnails.length > 2 && (
            <Animated.View style={[style.lightRatingContainer, { opacity: 1 }]}>
              <LightRatingButton
                variant="medium"
                lightRatingValue={lightRatingValues.ranking ?? 0}
                onPress={() =>
                  onLightRatingPress({
                    name: lightRatingValues?.name,
                    lightRating: lightRatingValues?.ranking ?? null
                  })
                }
              />
            </Animated.View>
          )}
          <Image
            cachePolicy={'memory-disk'}
            style={[style.whFull, style.squareAspectRatio]}
            source={firebaseImage(thumbnails.at(0), ThumbSize.Large)}
            contentFit="cover"
          />
        </button>

        <div className="flex flex-1 flex-col gap-y-2">
          <div className="flex flex-1 flex-row gap-x-2">
            <button className="flex-1 relative" onClick={() => handleOpenGallery(1)}>
              {lightRatingValues && thumbnails.length === 2 && (
                <Animated.View style={[style.lightRatingContainer, { opacity: 1 }]}>
                  <LightRatingButton
                    variant="medium"
                    lightRatingValue={lightRatingValues.ranking ?? 0}
                    onPress={() =>
                      onLightRatingPress({
                        name: lightRatingValues?.name,
                        lightRating: lightRatingValues?.ranking ?? null
                      })
                    }
                  />
                </Animated.View>
              )}

              {/* Second Image */}
              <Image
                cachePolicy={'memory-disk'}
                style={style.whFull}
                source={firebaseImage(thumbnails.at(1), ThumbSize.Large)}
                contentFit="cover"
              />
            </button>
            {thumbnails.length > 4 && (
              // Third Image
              <button style={{ flex: 1 }} onClick={() => handleOpenGallery(2)}>
                <Image
                  cachePolicy={'memory-disk'}
                  style={style.whFull}
                  source={firebaseImage(thumbnails.at(2), ThumbSize.Large)}
                  contentFit="cover"
                />
              </button>
            )}
          </div>
          {thumbnails.length > 2 && (
            <div className="flex flex-1 flex-row gap-x-2">
              {/* Fourth or Third Image */}
              <button className="flex-1" onClick={() => handleOpenGallery(thumbnails.length > 4 ? 3 : 2)}>
                <Image
                  cachePolicy={'memory-disk'}
                  style={style.whFull}
                  source={firebaseImage(thumbnails.at(thumbnails.length > 4 ? 3 : 2), ThumbSize.Large)}
                  contentFit="cover"
                />
              </button>
              {thumbnails.length > 3 && (
                <div className="flex-1 relative">
                  {thumbnails.length > 5 && (
                    <button
                      onClick={() => handleOpenGallery(0)}
                      className="absolute inset-0 bg-black bg-opacity-40 hover:bg-opacity-50 z-10 cursor-pointer flex items-center justify-center">
                      <div className="flex flex-col items-center">
                        <MaterialIcons name="photo-library" size={36} color="white" />
                        <span className="text-white">View all</span>
                      </div>
                    </button>
                  )}

                  {/* Fifth or Fourth Image */}
                  <button
                    className="w-full h-full top-0 absolute"
                    onClick={() => handleOpenGallery(thumbnails.length > 4 ? 4 : 3)}>
                    <Image
                      cachePolicy={'memory-disk'}
                      style={style.whFull}
                      source={firebaseImage(thumbnails.at(thumbnails.length > 4 ? 4 : 3), ThumbSize.Large)}
                      contentFit="cover"
                    />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const style = StyleSheet.create({
  lightRatingContainer: {
    position: 'absolute',
    right: 10,
    bottom: 10,
    zIndex: 10,
    elevation: 10
  },

  squareAspectRatio: {
    aspectRatio: 1
  },

  whFull: {
    width: '100%',
    height: '100%'
  }
});

export default DetailsHeaderGallery;
